import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import posthog from 'posthog-js';
import {
    FaDatabase, FaChartBar, FaArrowRight, FaClock, FaLayerGroup,
    FaCheck, FaShoppingCart, FaMobileAlt, FaCreditCard,
    FaChartPie, FaSearch, FaCar,
    FaPlay, FaCheckCircle, FaTimes, FaBolt, FaTrophy
} from 'react-icons/fa';
import { progressService } from '../../services/progressService';
import { useAuth } from '../../context/AuthContext';
import SEO from '../common/SEO';
import ParticleBackground from '../common/ParticleBackground';

// ─── Module definitions ──────────────────────────────────────────────────────

const SQL_MODULES = [
    {
        id: 'sql-fundamentals',
        track: 'SQL',
        title: 'SQL Fundamentals',
        category: 'SQL Basics',
        duration: '45 mins',
        challengeCount: 12,
        description: 'Learn SELECT, WHERE, ORDER BY, GROUP BY, and aggregate functions against a real e-commerce dataset.',
        link: '/exercises/thinking-in-data/case/0/challenge/0',
        icon: FaDatabase,
        keywords: ['select', 'where', 'order by', 'group by', 'count', 'sum', 'avg', 'aggregate', 'filter', 'sort', 'basics', 'beginner'],
    },
    {
        id: 'sql-intermediate',
        track: 'SQL',
        title: 'SQL Intermediate',
        category: 'Joins & Subqueries',
        duration: '60 mins',
        challengeCount: 14,
        description: 'Master JOIN types, subqueries, CASE WHEN logic, and window functions on multi-table schemas.',
        link: '/exercises/thinking-in-data/case/1/challenge/0',
        icon: FaDatabase,
        keywords: ['join', 'inner join', 'left join', 'subquery', 'case when', 'window function', 'rank', 'cte', 'with clause'],
    },
    {
        id: 'paper-plus',
        track: 'SQL',
        title: 'PaperPlus: Sales Analytics',
        category: 'Sales Analytics',
        duration: '60 mins',
        challengeCount: 7,
        description: 'Analyse sales performance, regional trends, and rep leaderboards for a B2B paper supplier.',
        link: '/exercises/thinking-in-data/case/2/challenge/0',
        icon: FaShoppingCart,
        keywords: ['sales', 'revenue', 'regions', 'leaderboard', 'b2b', 'performance', 'reps', 'accounts'],
    },
    {
        id: 'product-mix',
        track: 'SQL',
        title: 'ProductMix: Upsell Analytics',
        category: 'Sales Analytics',
        duration: '60 mins',
        challengeCount: 7,
        description: 'Uncover upsell opportunities, cross-sell leads, and build a training ROI model using CTEs.',
        link: '/exercises/thinking-in-data/case/3/challenge/0',
        icon: FaShoppingCart,
        keywords: ['upsell', 'cross-sell', 'roi', 'cte', 'having', 'case when', 'conversion', 'printing', 'delivery'],
    },
    {
        id: 'analytics',
        track: 'SQL',
        title: 'UrbanGo: Product Analytics',
        category: 'Product Analytics',
        duration: '60 mins',
        challengeCount: 7,
        description: 'Track signups, time-to-activation, engagement depth, and early retention for a ride-sharing app.',
        link: '/exercises/thinking-in-data/case/4/challenge/0',
        icon: FaMobileAlt,
        keywords: ['product', 'activation', 'retention', 'engagement', 'cohort', 'sessions', 'signups', 'ride-sharing'],
    },
    {
        id: 'fintech',
        track: 'SQL',
        title: 'PayFlow: Fintech Analytics',
        category: 'Fintech',
        duration: '60 mins',
        challengeCount: 6,
        description: 'Build FX-normalised TPV reports, flag high-risk customers, and run cohort quality analysis.',
        link: '/exercises/thinking-in-data/case/5/challenge/0',
        icon: FaCreditCard,
        keywords: ['fintech', 'tpv', 'fx', 'currency', 'chargeback', 'refund', 'cohort', 'risk', 'payments'],
    },
];

const DATA_VIZ_MODULES = [
    {
        id: 'data-viz-intro',
        track: 'DataViz',
        title: 'Foundations: Core Chart Types',
        category: 'Foundations',
        duration: '20 mins',
        description: 'Master the five foundational chart types: bar, line, scatter, histogram, and pie charts.',
        link: '/exercises/data-viz-intro',
        icon: FaChartBar,
        keywords: ['bar chart', 'line chart', 'scatter', 'histogram', 'pie', 'foundations', 'chart types', 'basics'],
    },
    {
        id: 'data-viz-intermediate',
        track: 'DataViz',
        title: 'Beyond the Basics',
        category: 'Advanced Charts',
        duration: '25 mins',
        description: 'Explore advanced viz types: box plots, heatmaps, treemaps, and combination charts.',
        link: '/exercises/data-viz-intermediate',
        icon: FaChartPie,
        keywords: ['box plot', 'heatmap', 'treemap', 'combo chart', 'advanced', 'distribution'],
    },
    {
        id: 'data-viz-1',
        track: 'DataViz',
        title: 'UK Growth Investigation',
        category: 'Case Study',
        duration: '45 mins',
        description: 'Build an end-to-end growth analysis dashboard for a UK e-commerce company.',
        link: '/exercises/data-viz-1',
        icon: FaSearch,
        keywords: ['growth', 'e-commerce', 'dashboard', 'uk', 'investigation', 'trends'],
    },
    {
        id: 'data-viz-2',
        track: 'DataViz',
        title: 'Customer Financial Behaviour',
        category: 'Case Study',
        duration: '45 mins',
        description: 'Visualise transaction patterns, spending categories, and customer lifetime value trends.',
        link: '/exercises/data-viz-2',
        icon: FaCreditCard,
        keywords: ['transactions', 'spending', 'ltv', 'customer', 'finance', 'behaviour'],
    },
    {
        id: 'data-viz-3',
        track: 'DataViz',
        title: 'Ride-Sharing Operations',
        category: 'Case Study',
        duration: '45 mins',
        description: 'Analyse trip volumes, peak hours, driver utilisation, and revenue by city.',
        link: '/exercises/data-viz-3',
        icon: FaCar,
        keywords: ['ride-sharing', 'trips', 'peak hours', 'utilisation', 'operations', 'city'],
    },
];

// ─── LocalStorage helpers ────────────────────────────────────────────────────

const readSqlFromLS = () => {
    try { const s = localStorage.getItem('thinking-in-dataProgress'); return s ? (JSON.parse(s).completed || []).length : 0; } catch { return 0; }
};
const readSqlCasesFromLS = () => {
    try { const s = localStorage.getItem('thinking-in-dataProgress'); return s ? new Set(JSON.parse(s).completedCases || []) : new Set(); } catch { return new Set(); }
};
const readDvCountFromLS = () => {
    try { const s = localStorage.getItem('data-vizProgress'); return s ? (JSON.parse(s).completed || []).length : 0; } catch { return 0; }
};
const readDvIdsFromLS = () => {
    try { const s = localStorage.getItem('data-vizProgress'); return new Set(JSON.parse(s || '{}').completed || []); } catch { return new Set(); }
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const ProgressBar = ({ percent, colorClass }) => (
    <div className="w-full bg-gray-100 dark:bg-gray-700/50 rounded-full h-[3px] overflow-hidden">
        <div className={`h-full rounded-full ${colorClass}`} style={{ width: `${percent}%` }} />
    </div>
);

const ModuleCard = ({ mod, isDone, query }) => {
    const isSQL = mod.track === 'SQL';
    const Icon = mod.icon;

    const t = isSQL ? {
        band: isDone ? 'from-green-400 to-emerald-500' : 'from-orange-500 to-amber-400',
        iconBg: isDone ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/40' : 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800/40',
        iconText: isDone ? 'text-green-500' : 'text-orange-500',
        cardHover: isDone
            ? 'hover:bg-green-50/40 hover:border-green-300/80 dark:hover:bg-[#0d1f18] dark:hover:border-green-600/35'
            : 'hover:bg-orange-50/30 hover:border-orange-300/80 dark:hover:bg-[#1c1409] dark:hover:border-orange-500/30',
        btn: isDone ? 'bg-green-500 hover:bg-green-400' : 'bg-orange-500 hover:bg-orange-400',
    } : {
        band: isDone ? 'from-green-400 to-emerald-500' : 'from-orange-500 to-amber-400',
        iconBg: isDone ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/40' : 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800/40',
        iconText: isDone ? 'text-green-500' : 'text-orange-500',
        cardHover: isDone
            ? 'hover:bg-green-50/40 hover:border-green-300/80 dark:hover:bg-[#0d1f18] dark:hover:border-green-600/35'
            : 'hover:bg-orange-50/30 hover:border-orange-300/80 dark:hover:bg-[#1c1409] dark:hover:border-orange-500/30',
        btn: isDone ? 'bg-green-500 hover:bg-green-400' : 'bg-orange-500 hover:bg-orange-400',
    };

    const highlight = (text) => {
        if (!query) return text;
        const re = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        return text.split(re).map((p, i) =>
            re.test(p) ? <mark key={i} className="bg-amber-200 dark:bg-amber-700/60 text-gray-900 dark:text-white rounded px-0.5">{p}</mark> : p
        );
    };

    return (
        <Link
            to={mod.link}
            className="block group"
            onClick={() => posthog.capture('module_started', { module_id: mod.id, module_title: mod.title, track: mod.track })}
        >
            <div className={`flex flex-col h-full bg-white dark:bg-[#0f1c2e] rounded-2xl border border-gray-200 dark:border-gray-800/80 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 ${t.cardHover} ${isDone ? 'border-green-200/70 dark:border-green-900/40' : ''}`}>
                <div className={`h-[3px] bg-gradient-to-r ${t.band} flex-shrink-0`} />
                <div className="p-5 flex flex-col flex-1 gap-3">
                    <div className="flex items-start gap-3">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 border transition-colors ${t.iconBg}`}>
                            {isDone ? <FaCheckCircle className="text-green-500" size={14} /> : <Icon className={t.iconText} size={14} />}
                        </div>
                        <div className="min-w-0 flex-1 pt-0.5">
                            <p className="text-[10px] text-gray-400 dark:text-gray-500 font-medium mb-0.5">{mod.category}</p>
                            <h3 className="text-sm font-bold text-gray-900 dark:text-white leading-snug">
                                {highlight(mod.title)}
                            </h3>
                        </div>
                        {isDone && (
                            <span className="flex-shrink-0 mt-0.5 text-[10px] font-bold text-green-500 flex items-center gap-1">
                                <FaCheck size={8} />Done
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed flex-1">{highlight(mod.description)}</p>
                    <div className="flex items-center gap-3 text-[11px] text-gray-400 dark:text-gray-500">
                        <span className="flex items-center gap-1"><FaClock size={9} />{mod.duration}</span>
                        {mod.challengeCount && <span className="flex items-center gap-1"><FaLayerGroup size={9} />{mod.challengeCount} challenges</span>}
                    </div>
                    <button className={`w-full mt-1 py-2 rounded-lg font-bold text-xs flex items-center justify-center gap-1.5 text-white transition-all shadow-sm group-hover:shadow-md active:scale-[0.98] ${t.btn}`}>
                        {isDone ? <><FaCheckCircle size={10} />Review</> : <><FaPlay size={9} />Start module<FaArrowRight size={9} /></>}
                    </button>
                </div>
            </div>
        </Link>
    );
};

const TrackSection = ({ track, description, modules, completedCount, totalCount, progressColorClass, ctaHref, isSQL, isCompleted, query }) => {
    const percent = Math.round((completedCount / totalCount) * 100);
    const borderColor = 'border-orange-100 dark:border-orange-900/30';
    const iconBg = 'bg-orange-50 dark:bg-orange-900/20 border-orange-100 dark:border-orange-800/40';
    const pillBg = 'bg-orange-100 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400';
    const ctaBtn = 'bg-orange-500 hover:bg-orange-400';
    const percentColor = 'text-orange-500';

    return (
        <section>
            <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-5 border-b ${borderColor}`}>
                <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border ${iconBg}`}>
                        {isSQL ? <FaDatabase className="text-orange-500" size={16} /> : <FaChartBar className="text-orange-500" size={16} />}
                    </div>
                    <div>
                        <div className="flex items-center gap-2 mb-0.5">
                            <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${pillBg}`}>Track</span>
                            <span className="text-[11px] text-gray-400 dark:text-gray-500 font-medium">{completedCount} of {totalCount} complete</span>
                        </div>
                        <h2 className="text-xl font-extrabold text-gray-900 dark:text-white leading-tight tracking-tight">{track}</h2>
                        {description && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 hidden sm:block">{description}</p>}
                    </div>
                </div>
                <div className="flex items-center gap-4 sm:flex-shrink-0">
                    <div className="hidden sm:flex flex-col items-end gap-1.5 min-w-[110px]">
                        <span className={`text-[11px] font-bold ${percentColor}`}>{percent}% complete</span>
                        <ProgressBar percent={percent} colorClass={progressColorClass} />
                    </div>
                    <Link to={ctaHref}>
                        <button className={`px-4 py-2 rounded-xl font-bold text-xs text-white flex items-center gap-2 transition-all whitespace-nowrap shadow-md ${ctaBtn}`}>
                            {completedCount > 0 ? 'Continue' : 'Start'} <FaArrowRight size={10} />
                        </button>
                    </Link>
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {modules.map(mod => (
                    <ModuleCard key={mod.id} mod={mod} isDone={isCompleted(mod)} query={query} />
                ))}
            </div>
        </section>
    );
};

// ─── Main component ──────────────────────────────────────────────────────────

const PracticeDashboard = () => {
    const { user } = useAuth();
    const [query, setQuery] = useState('');
    const [activeFilter, setActiveFilter] = useState('all');

    const [sqlCompletedCount, setSqlCompletedCount] = useState(() => readSqlFromLS());
    const [dvCompletedCount, setDvCompletedCount] = useState(() => readDvCountFromLS());
    const [sqlCompletedCases, setSqlCompletedCases] = useState(() => readSqlCasesFromLS());
    const [dvCompletedIds, setDvCompletedIds] = useState(() => readDvIdsFromLS());

    useEffect(() => {
        if (!user) return;
        (async () => {
            try {
                const p = await progressService.loadExerciseProgress('thinking-in-data');
                const cases = p.completedCases || [];
                setSqlCompletedCount(cases.length);
                setSqlCompletedCases(new Set(cases));
            } catch { }
            try {
                const dvProgress = await progressService.loadExerciseProgress('data-viz').catch(() => ({ completedCases: [] }));
                const done = new Set(dvProgress.completedCases || []);
                setDvCompletedCount(done.size);
                setDvCompletedIds(done);
            } catch { }
        })();
    }, [user]);

    const isCompleted = (mod) => mod.track === 'SQL' ? sqlCompletedCases.has(mod.id) : dvCompletedIds.has(mod.id);
    const searchActive = query.trim().length > 0;

    const filteredSql = useMemo(() => {
        if (!searchActive && activeFilter === 'viz') return [];
        if (!searchActive) return SQL_MODULES;
        const q = query.toLowerCase();
        return SQL_MODULES.filter(m =>
            m.title.toLowerCase().includes(q) ||
            m.description.toLowerCase().includes(q) ||
            m.category.toLowerCase().includes(q) ||
            (m.keywords || []).some(k => k.toLowerCase().includes(q))
        );
    }, [query, activeFilter, searchActive]);

    const filteredViz = useMemo(() => {
        if (!searchActive && activeFilter === 'sql') return [];
        if (!searchActive) return DATA_VIZ_MODULES;
        const q = query.toLowerCase();
        return DATA_VIZ_MODULES.filter(m =>
            m.title.toLowerCase().includes(q) ||
            m.description.toLowerCase().includes(q) ||
            m.category.toLowerCase().includes(q) ||
            (m.keywords || []).some(k => k.toLowerCase().includes(q))
        );
    }, [query, activeFilter, searchActive]);

    const totalResults = filteredSql.length + filteredViz.length;

    return (
        <>
            <SEO
                title="Practice Hub | DataHelix"
                description="Choose your learning track - master SQL through real business cases or build data visualization skills with guided chart exercises."
            />
            <div className="min-h-screen bg-[#f8f9fb] dark:bg-[#080f1a] font-sans transition-colors duration-300">

                {/* ── Hero ── */}
                <div className="relative bg-white dark:bg-[#0a1220] border-b border-gray-200 dark:border-gray-800/80 pt-16 pb-20 px-4 lg:px-8 overflow-hidden">

                    {/* Particle constellation */}
                    <ParticleBackground particleCount={50} maxDist={110} speed={0.25} />

                    {/* Glow blobs */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/4 w-[600px] h-[300px] bg-orange-500/[0.14] dark:bg-orange-500/10 rounded-full blur-[100px] pointer-events-none" />
                    <div className="absolute bottom-0 left-1/4 w-[400px] h-[200px] bg-orange-500/[0.12] dark:bg-orange-500/10 rounded-full blur-[80px] pointer-events-none" />

                    <div className="relative max-w-3xl mx-auto text-center">
                        {/* Badge */}
                        <div className="inline-flex items-center gap-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200/80 dark:border-orange-800/40 rounded-full px-3.5 py-1.5 mb-6">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500" />
                            </span>
                            <span className="text-[10px] font-bold uppercase tracking-widest text-orange-600 dark:text-orange-400">DataHelix Practice Hub</span>
                        </div>

                        <h1 className="text-4xl md:text-[3.5rem] font-extrabold mb-4 text-gray-900 dark:text-white leading-[1.1] tracking-tight">
                            Learn by doing.{" "}
                            <span className="bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 text-transparent bg-clip-text">
                                Every module.
                            </span>
                        </h1>
                        <p className="text-gray-500 dark:text-gray-400 max-w-lg mx-auto text-base leading-relaxed mb-8">
                            Practice on real datasets. Gain the intuition that data roles actually demand.
                        </p>

                        {/* Search */}
                        <div className="max-w-xl mx-auto mb-10">
                            <div className="relative flex items-center">
                                <FaSearch className="absolute left-4 text-gray-400 dark:text-gray-500 pointer-events-none" size={13} />
                                <input
                                    type="text"
                                    value={query}
                                    onChange={e => setQuery(e.target.value)}
                                    placeholder="Search modules - try 'joins', 'cte', 'retention', 'heatmap'…"
                                    className="w-full pl-10 pr-10 py-3.5 rounded-xl text-sm font-medium bg-white dark:bg-[#0f1c2e] border border-gray-200 dark:border-gray-700/80 shadow-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-400 dark:focus:border-orange-500 transition-all"
                                />
                                {query && (
                                    <button onClick={() => setQuery('')} className="absolute right-3.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                                        <FaTimes size={12} />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Stats ribbon */}
                        <div className="inline-flex items-center gap-0 rounded-2xl border border-gray-200 dark:border-gray-700/60 bg-white/80 dark:bg-[#0f1c2e]/80 backdrop-blur-sm shadow-sm overflow-hidden divide-x divide-gray-200 dark:divide-gray-700/60">
                            {[
                                { icon: FaDatabase,  value: '6',    label: 'SQL modules',      color: 'text-orange-500' },
                                { icon: FaChartBar,  value: '5',    label: 'DataViz modules',  color: 'text-orange-500' },
                                { icon: FaBolt,      value: '85+',  label: 'Challenges',       color: 'text-amber-500' },
                                { icon: FaTrophy,    value: '100%', label: 'Free',             color: 'text-emerald-500' },
                            ].map(({ icon: Icon, value, label, color }) => (
                                <div key={label} className="flex flex-col items-center gap-1 px-6 py-3.5">
                                    <div className={`text-[1.1rem] font-extrabold leading-none tabular-nums ${color}`}>{value}</div>
                                    <div className="flex items-center gap-1 mt-0.5">
                                        <Icon className="text-gray-400 dark:text-gray-500" size={9} />
                                        <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium whitespace-nowrap">{label}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ── Body ── */}
                <div className="relative">
                    {/* Particle constellation */}
                    <ParticleBackground particleCount={80} maxDist={130} speed={0.3} />
                    {/* Glow blobs behind track cards */}
                    <div className="absolute top-10 left-0 w-[500px] h-[400px] bg-orange-500/[0.10] dark:bg-orange-500/[0.06] rounded-full blur-[120px] pointer-events-none" />
                    <div className="absolute top-10 right-0 w-[500px] h-[400px] bg-amber-500/[0.10] dark:bg-amber-500/[0.06] rounded-full blur-[120px] pointer-events-none" />
                    {/* Glow blob behind modules */}
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[700px] h-[300px] bg-orange-400/[0.08] dark:bg-orange-400/[0.05] rounded-full blur-[100px] pointer-events-none" />
                <div className="relative max-w-6xl mx-auto px-4 lg:px-8 py-14 space-y-16">

                    {/* ── Track hero cards ── */}
                    {!searchActive && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

                            {/* SQL Track */}
                            <div className="relative flex flex-col bg-white dark:bg-[#0a1525] rounded-2xl border border-gray-200 dark:border-gray-800/80 shadow-md overflow-hidden group hover:shadow-2xl hover:border-orange-300/70 dark:hover:border-orange-600/50 transition-all duration-300">
                                {/* Top gradient band */}
                                <div className="h-[3px] bg-gradient-to-r from-orange-500 via-amber-400 to-red-500 flex-shrink-0" />

                                {/* Ambient glow */}
                                <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-orange-500/10 rounded-full blur-2xl pointer-events-none" />

                                <div className="relative p-7 flex flex-col flex-1 gap-5">
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-orange-500/30">
                                            <FaDatabase className="text-white" size={18} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <span className="text-[9px] font-black uppercase tracking-widest text-orange-500 dark:text-orange-400">SQL Track</span>
                                            <h2 className="text-xl font-extrabold text-gray-900 dark:text-white leading-tight mt-0.5">Thinking in Data</h2>
                                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">From SELECT basics to production-grade analytics</p>
                                        </div>
                                    </div>

                                    <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                                        Six business cases built on real schemas. Filter, aggregate, join, and model data the way analysts do at work - no toy examples.
                                    </p>

                                    <div className="flex flex-wrap gap-2">
                                        {['Filters & Aggregates', 'JOINs & CTEs', 'Window Functions', 'Case Studies'].map(tag => (
                                            <span key={tag} className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-orange-50 dark:bg-orange-900/25 text-orange-600 dark:text-orange-400 border border-orange-100 dark:border-orange-800/40">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>

                                    <div className="flex items-center gap-3 text-[11px] text-gray-400 dark:text-gray-500">
                                        <span className="flex items-center gap-1"><FaLayerGroup size={9} />{SQL_MODULES.length} modules</span>
                                        <span className="flex items-center gap-1"><FaClock size={9} />~6 hours</span>
                                        <span className="font-bold text-orange-500">45+ challenges</span>
                                    </div>

                                    <div>
                                        <div className="flex justify-between text-[10px] font-semibold text-gray-400 dark:text-gray-500 mb-1.5">
                                            <span>{sqlCompletedCount} of {SQL_MODULES.length} modules complete</span>
                                            <span className="text-orange-500">{Math.round((sqlCompletedCount / SQL_MODULES.length) * 100)}%</span>
                                        </div>
                                        <ProgressBar percent={Math.round((sqlCompletedCount / SQL_MODULES.length) * 100)} colorClass="bg-gradient-to-r from-orange-500 to-amber-400" />
                                    </div>

                                    <Link to="/exercises/thinking-in-data" className="mt-auto">
                                        <button className="w-full py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-white transition-all shadow-md shadow-orange-900/20 group-hover:shadow-xl group-hover:shadow-orange-500/20">
                                            {sqlCompletedCount > 0 ? 'Continue' : 'Start'} SQL track <FaArrowRight size={11} />
                                        </button>
                                    </Link>
                                </div>
                            </div>

                            {/* Data Viz Track */}
                            <div className="relative flex flex-col bg-white dark:bg-[#0a1525] rounded-2xl border border-gray-200 dark:border-gray-800/80 shadow-md overflow-hidden group hover:shadow-2xl hover:border-orange-300/70 dark:hover:border-orange-600/50 transition-all duration-300">
                                <div className="h-[3px] bg-gradient-to-r from-orange-500 via-amber-400 to-red-500 flex-shrink-0" />

                                {/* Ambient glow */}
                                <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-orange-500/10 rounded-full blur-2xl pointer-events-none" />

                                <div className="relative p-7 flex flex-col flex-1 gap-5">
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-orange-500/30">
                                            <FaChartBar className="text-white" size={18} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <span className="text-[9px] font-black uppercase tracking-widest text-orange-500 dark:text-orange-400">Data Viz Track</span>
                                            <h2 className="text-xl font-extrabold text-gray-900 dark:text-white leading-tight mt-0.5">Visualizing Data</h2>
                                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">From chart fundamentals to insight storytelling</p>
                                        </div>
                                    </div>

                                    <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                                        Five modules covering core chart types through real case studies. Learn when to use which chart and why - then build dashboards that actually communicate.
                                    </p>

                                    <div className="flex flex-wrap gap-2">
                                        {['Core Chart Types', 'Advanced Charts', 'Growth Analysis', 'Case Studies'].map(tag => (
                                            <span key={tag} className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-orange-50 dark:bg-orange-900/25 text-orange-600 dark:text-orange-400 border border-orange-100 dark:border-orange-800/40">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>

                                    <div className="flex items-center gap-3 text-[11px] text-gray-400 dark:text-gray-500">
                                        <span className="flex items-center gap-1"><FaLayerGroup size={9} />{DATA_VIZ_MODULES.length} modules</span>
                                        <span className="flex items-center gap-1"><FaClock size={9} />~3 hours</span>
                                        <span className="font-bold text-orange-500">40+ challenges</span>
                                    </div>

                                    <div>
                                        <div className="flex justify-between text-[10px] font-semibold text-gray-400 dark:text-gray-500 mb-1.5">
                                            <span>{dvCompletedCount} of {DATA_VIZ_MODULES.length} modules complete</span>
                                            <span className="text-orange-500">{Math.round((dvCompletedCount / DATA_VIZ_MODULES.length) * 100)}%</span>
                                        </div>
                                        <ProgressBar percent={Math.round((dvCompletedCount / DATA_VIZ_MODULES.length) * 100)} colorClass="bg-gradient-to-r from-orange-500 to-amber-400" />
                                    </div>

                                    <Link to="/exercises/data-viz" className="mt-auto">
                                        <button className="w-full py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-white transition-all shadow-md shadow-orange-900/20 group-hover:shadow-xl group-hover:shadow-orange-500/20">
                                            {dvCompletedCount > 0 ? 'Continue' : 'Start'} Data Viz track <FaArrowRight size={11} />
                                        </button>
                                    </Link>
                                </div>
                            </div>

                        </div>
                    )}

                    {/* Filter pills - only when not searching */}
                    {!searchActive && (
                        <div className="flex items-center gap-2 -mb-8">
                            {[
                                { key: 'all', label: 'All tracks' },
                                { key: 'sql', label: 'SQL only' },
                                { key: 'viz', label: 'Data Viz only' },
                            ].map(({ key, label }) => (
                                <button
                                    key={key}
                                    onClick={() => setActiveFilter(key)}
                                    className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all duration-200 ${
                                        activeFilter === key
                                            ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 shadow-sm'
                                            : 'bg-white dark:bg-gray-800/80 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500'
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Search results label */}
                    {searchActive && (
                        <div className="-mb-8">
                            <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                                {totalResults === 0
                                    ? 'No results'
                                    : <>{totalResults} module{totalResults !== 1 ? 's' : ''} found for{' '}<span className="text-orange-500">"{query}"</span></>
                                }
                            </p>
                        </div>
                    )}

                    {/* No results */}
                    {searchActive && totalResults === 0 && (
                        <div className="flex flex-col items-center justify-center py-20 gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                                <FaSearch className="text-gray-400" size={20} />
                            </div>
                            <div className="text-center">
                                <p className="font-bold text-gray-700 dark:text-gray-300">No modules found</p>
                                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Try "joins", "cte", "retention", or "heatmap"</p>
                            </div>
                            <button onClick={() => setQuery('')} className="text-xs font-bold text-orange-500 hover:text-orange-400 transition-colors">
                                Clear search
                            </button>
                        </div>
                    )}

                    {/* SQL Track */}
                    {filteredSql.length > 0 && (
                        <TrackSection
                            track="SQL - Thinking in Data"
                            description="Six cases from SELECT basics to fintech analytics. Write real queries against real business data."
                            modules={filteredSql}
                            completedCount={sqlCompletedCount}
                            totalCount={SQL_MODULES.length}
                            progressColorClass="bg-gradient-to-r from-orange-500 to-amber-400"
                            ctaHref="/exercises/thinking-in-data"
                            isSQL={true}
                            isCompleted={isCompleted}
                            query={query}
                        />
                    )}

                    {/* Data Viz Track */}
                    {filteredViz.length > 0 && (
                        <TrackSection
                            track="Data Viz - Visualizing Data"
                            description="Five modules from core chart types to real case studies. Build charts that tell a story."
                            modules={filteredViz}
                            completedCount={dvCompletedCount}
                            totalCount={DATA_VIZ_MODULES.length}
                            progressColorClass="bg-gradient-to-r from-orange-500 to-amber-400"
                            ctaHref="/exercises/data-viz"
                            isSQL={false}
                            isCompleted={isCompleted}
                            query={query}
                        />
                    )}

                </div>
                </div>{/* /body relative wrapper */}
            </div>
        </>
    );
};

export default PracticeDashboard;
